import React, { useState, useEffect, useCallback } from "react";
import api, {
  getProducts, createProduct, updateProduct, deleteProduct,
  uploadImage, reorderSkus, getStats, exportCsvUrl,
} from "./api";
import ProductModal from "./ProductModal";
import Login from "./Login";
import Precificacao from "./Precificacao";
import "./App.css";

const TIPOS = ["Todos", "Anel Feminino", "Solitário Feminino", "Anel Masculino"];

function App() {
  const [token, setToken] = useState(localStorage.getItem("lyore_token"));
  const [page, setPage] = useState("produtos"); // "produtos" | "precificacao"
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [filtro, setFiltro] = useState("Todos");
  const [busca, setBusca] = useState("");
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);

  // Adicionar token em todas as requisições
  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("lyore_token", token);
    } else {
      delete api.defaults.headers.common["Authorization"];
      localStorage.removeItem("lyore_token");
    }
  }, [token]);

  const handleLogin = async (email, password) => {
    const res = await api.post("/api/auth/login", { email, password });
    setToken(res.data.token);
  };

  const handleLogout = () => {
    setToken(null);
    setProducts([]);
    setStats({});
  };

  const fetchProducts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (filtro !== "Todos") params.tipo = filtro;
      if (busca) params.busca = busca;
      const [prodRes, statsRes] = await Promise.all([getProducts(params), getStats()]);
      setProducts(prodRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) setToken(null);
    }
    setLoading(false);
  }, [filtro, busca, token]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSave = async (data, imageFile) => {
    let saved;
    if (modal.mode === "edit") {
      saved = await updateProduct(modal.product.id, data);
    } else {
      saved = await createProduct(data);
    }
    if (imageFile) {
      await uploadImage(saved.data.id, imageFile);
    }
    setModal(null);
    fetchProducts();
  };

  const handleDelete = async (id, nome) => {
    if (window.confirm(`Remover "${nome}"?`)) {
      await deleteProduct(id);
      fetchProducts();
    }
  };

  const handleReorderSkus = async () => {
    if (window.confirm("Reordenar todos os SKUs?")) {
      await reorderSkus();
      fetchProducts();
    }
  };

  if (!token) return <Login onLogin={handleLogin} />;

  return (
    <div className="app">
      <header className="header">
        <div className="header-left">
          <h1>Lyore Manager</h1>
          <nav className="header-nav">
            <button
              className={`nav-tab ${page === "produtos" ? "active" : ""}`}
              onClick={() => setPage("produtos")}
            >
              Produtos
            </button>
            <button
              className={`nav-tab ${page === "precificacao" ? "active" : ""}`}
              onClick={() => setPage("precificacao")}
            >
              Precificacao
            </button>
          </nav>
        </div>
        <button className="btn-logout" onClick={handleLogout}>Sair</button>
      </header>

      {page === "precificacao" ? (
        <Precificacao token={token} />
      ) : (
        <>
          <div className="toolbar">
            <button className="btn btn-primary" onClick={() => setModal({ mode: "new" })}>
              + Novo Produto
            </button>
            <a className="btn btn-secondary" href={exportCsvUrl()} download>
              Exportar CSV
            </a>
            <button className="btn btn-secondary" onClick={handleReorderSkus}>
              Reordenar SKUs
            </button>
          </div>

          <div className="filters">
            <div className="filter-tabs">
              {TIPOS.map((t) => (
                <button
                  key={t}
                  className={`tab ${filtro === t ? "active" : ""}`}
                  onClick={() => setFiltro(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por nome ou SKU..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>

          <div className="stats-bar">
            Total: {stats.total || 0} | Aneis Fem: {stats.aneis_femininos || 0} |
            Solitarios: {stats.solitarios_femininos || 0} | Masc: {stats.aneis_masculinos || 0} |
            Exibindo: {products.length}
          </div>

          {loading ? (
            <div className="loading">Carregando...</div>
          ) : (
            <div className="product-grid">
              {products.map((p) => (
                <div key={p.id} className="product-card">
                  <div className="card-image">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.nome} />
                    ) : (
                      <div className="no-image">Sem imagem</div>
                    )}
                  </div>
                  <div className="card-body">
                    <span className="card-sku">{p.sku}</span>
                    <h3 className="card-name">{p.nome}</h3>
                    <span className="card-tipo">{p.tipo}</span>
                    <div className="card-prices">
                      <span className="price-original">R$ {p.preco}</span>
                      <span className="price-promo">R$ {p.preco_promo}</span>
                    </div>
                    <div className="card-categories">{p.categorias}</div>
                  </div>
                  <div className="card-actions">
                    <button className="btn btn-sm" onClick={() => setModal({ mode: "edit", product: p })}>
                      Editar
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p.id, p.nome)}>
                      Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {modal && (
            <ProductModal
              mode={modal.mode}
              product={modal.product}
              onSave={handleSave}
              onClose={() => setModal(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;
